import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { pluralize } from "utils/pluralize";
import Loader from "components/DesignSystem/Loader/loader";
import { CartModal } from "components/CartModal/CartModal";
import { useModal } from "hooks/useModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import authContext from "jwt_context&axios/authContext";
import { emptyApi } from "store/apis/emptyApi";
import { SetupModal } from "v2/components/entityDocuments/SetupModal";
import {
  TaxYearsData,
  useGetDetectedFilingsCountQuery,
  useGetEntitiesCartTotalQuery,
  useGetTaxYearsQuery,
} from "v2/store/taxSetupApi";
import { AccountSetupCard } from "v2/components/FilingsDashboard/AccountSetupCard";
import { useGroupId } from "v2/utils/useGroupId";
import { useOnboardingProgress } from "v2/utils/useOnboardingProgress";
import { DetectedFilingsCard } from "v2/components/FilingsDashboard/DetectedFilingsCard";
import { PastYearsCard } from "v2/components/FilingsDashboard/PastYearsCard";
import { AddPriorYearModal } from "v2/components/FilingsDashboard/AddPriorYearModal";
import { RemoveSeasonModal } from "v2/components/FilingsDashboard/RemoveSeasonModal";
import { MoreFilingsWaitingModal } from "v2/components/FilingsDashboard/MoreFilingsWaitingModal";
import { PaymentSuccessModal } from "v2/components/FilingsDashboard/PaymentSuccessModal";

const GROUPS_TAG = "Groups";

const sortDesc = (a: number, b: number): number => b - a;

export type DashboardEntity = {
  id: string;
  name: string;
  defaultYear: number;
  pastYears: number[];
  trackedYears: number[];
};

const availableYearsFor = (entity: DashboardEntity): number[] =>
  [...entity.pastYears].sort(sortDesc);

const selectedYearsFor = (entity: DashboardEntity): number[] =>
  [entity.defaultYear, ...entity.trackedYears].sort(sortDesc);

type DashboardContentProps = {
  userName: string;
  taxYears: TaxYearsData;
  amountToPay: number;
  cartItemsCount?: number;
  amountByEntityId: Record<string, number>;
  itemsCountByEntityId: Record<string, number>;
  groupId: string;
};

const DashboardContent = ({
  userName,
  taxYears,
  amountToPay,
  cartItemsCount,
  amountByEntityId,
  itemsCountByEntityId,
  groupId,
}: DashboardContentProps) => {
  const entities: DashboardEntity[] = taxYears.entities.map((entity) => ({
    id: entity.uuid,
    name: entity.name,
    defaultYear: Number(entity.default_tax_year),
    pastYears: entity.past_tax_years.map(Number),
    trackedYears: entity.tracked_years.map(Number),
  }));

  const dispatch = useDispatch();
  const { dispatch: setGroup, secondary_channel_url } =
    useCurrentGroupContext();

  const setupModal = useModal();
  const addYearModal = useModal();
  const removeYearModal = useModal();
  const paymentCartModal = useModal();
  const moreFilingsModal = useModal();
  const paymentSuccessModal = useModal();

  const [selectedEntityId, setSelectedEntityId] = useState(entities[0].id);

  const [addSeason, setAddSeason] = useState<number | null>(null);
  const [removeSeason, setRemoveSeason] = useState<number | null>(null);

  const [setupFocusEntityId, setSetupFocusEntityId] = useState<string | null>(
    null
  );

  const [hasPaidAnyFiling, setHasPaidAnyFiling] = useState(false);

  const [paymentQueue, setPaymentQueue] = useState<string[]>([]);
  const [paidEntity, setPaidEntity] = useState<{
    name: string;
    count: number;
  }>({ name: "", count: 0 });

  const { progressById, overallProgress, remainingQuestions, probes } =
    useOnboardingProgress({
      groupId,
      entityIds: entities.map((entity) => entity.id),
    });

  const { data: filingsCount } = useGetDetectedFilingsCountQuery(
    { groupId },
    { skip: !groupId }
  );

  const countById = Object.fromEntries(
    (filingsCount?.entities ?? []).map((entity) => [
      entity.uuid,
      entity.detected_filings_count,
    ])
  );

  const totalFilingCount = Object.values(countById).reduce(
    (sum, count) => sum + count,
    0
  );

  const isMultiEntity = entities.length > 1;

  const selectedEntity =
    entities.find((entity) => entity.id === selectedEntityId) ?? entities[0];

  const modalEntities = entities.map((entity) => ({
    id: entity.id,
    name: entity.name,
  }));

  const chipYears = availableYearsFor(selectedEntity);
  const selectedYears = selectedYearsFor(selectedEntity);

  const isSetupComplete =
    entities.length > 0 &&
    entities.every((entity) => (progressById[entity.id] ?? 0) === 100);

  const incompleteEntityIds = entities
    .filter((entity) => (progressById[entity.id] ?? 0) < 100)
    .map((entity) => entity.id);

  const filingLabel = pluralize(
    totalFilingCount,
    "filing requirement",
    "filing requirements"
  );

  let introText = `We identified ${filingLabel} based on your entity information. Complete your company profile below to identify more.`;

  if (isSetupComplete) {
    introText = `Your company profile is complete. Review the ${filingLabel} we identified below and proceed to payment to get them filed.`;
  }

  const currentPayEntityId = paymentQueue[0] ?? null;
  const currentPayEntity =
    entities.find((entity) => entity.id === currentPayEntityId) ?? null;

  const openAddYear = (year: number) => {
    setAddSeason(year);
    addYearModal.open();
  };

  const openRemoveYear = (year: number) => {
    setRemoveSeason(year);
    removeYearModal.open();
  };

  const openSetupModal = () => {
    setSetupFocusEntityId(null);
    setupModal.open();
  };

  const handleAddYearSuccess = () => {
    addYearModal.close();
    setSetupFocusEntityId(selectedEntity.id);
    setupModal.open();
  };

  const payableEntityIds = entities
    .filter((entity) => (itemsCountByEntityId[entity.id] ?? 0) > 0)
    .map((entity) => entity.id);

  const startReviewAndPay = () => {
    setPaymentQueue(payableEntityIds);
    paymentCartModal.open();
  };

  const handleReviewEntity = (entityId: string) => {
    const pendingIds = payableEntityIds.filter((id) => id !== entityId);

    setPaymentQueue([entityId, ...pendingIds]);
    paymentCartModal.open();
  };

  const isPaymentFlowOpen =
    paymentCartModal.isOpen ||
    moreFilingsModal.isOpen ||
    paymentSuccessModal.isOpen;

  useEffect(() => {
    if (!hasPaidAnyFiling || isPaymentFlowOpen) return;

    setGroup({ type: "SET_GROUP", payload: { has_paid_for_tax_filing: true } });
    dispatch(emptyApi.util.invalidateTags([GROUPS_TAG]));
  }, [hasPaidAnyFiling, isPaymentFlowOpen, setGroup, dispatch]);

  const handleCartPaid = () => {
    const [paidId, ...rest] = paymentQueue;
    const paid = entities.find((entity) => entity.id === paidId);

    setPaidEntity({
      name: paid?.name ?? "",
      count: paidId ? countById[paidId] ?? 0 : 0,
    });
    setPaymentQueue(rest);
    setHasPaidAnyFiling(true);
    paymentCartModal.close();

    const hasMorePending = isMultiEntity && rest.length > 0;
    if (hasMorePending) moreFilingsModal.open();
    else paymentSuccessModal.open();
  };

  const handleSingleFilingPaid = () => setHasPaidAnyFiling(true);

  const handleReviewNext = () => {
    moreFilingsModal.close();
    paymentCartModal.open();
  };

  const handlePayLater = () => {
    moreFilingsModal.close();
    setPaymentQueue([]);
  };

  const handleSuccessDone = () => {
    paymentSuccessModal.close();
    setPaymentQueue([]);
  };

  return (
    <>
      {probes}

      <div className="t-flex t-flex-col t-gap-0.5">
        <span className="t-text-title-h3 t-text-text-60">
          Hello, {userName}!
        </span>

        <span className="t-text-body t-text-text-30">{introText}</span>
      </div>

      <div className="t-flex t-flex-wrap t-items-start t-gap-5">
        <div className="t-w-[388px] t-shrink-0 sm:t-w-full">
          <AccountSetupCard
            progress={overallProgress}
            isSetupComplete={isSetupComplete}
            amountToPay={amountToPay}
            cartItemsCount={cartItemsCount}
            entities={entities}
            progressById={progressById}
            remainingQuestions={remainingQuestions}
            amountByEntityId={amountByEntityId}
            itemsCountByEntityId={itemsCountByEntityId}
            groupId={groupId}
            channelUrl={secondary_channel_url}
            onOpenSetupModal={openSetupModal}
            onReviewAndPay={startReviewAndPay}
            onReviewEntity={handleReviewEntity}
          />
        </div>

        <div className="t-flex t-min-w-[480px] t-flex-1 t-flex-col t-gap-3 sm:t-min-w-0">
          <DetectedFilingsCard
            isSetupComplete={isSetupComplete}
            onCompleteSetup={openSetupModal}
            entities={entities}
            countById={countById}
            groupId={groupId}
            selectedEntityId={selectedEntity.id}
            onSelectEntity={setSelectedEntityId}
            onPayAllFilings={handleReviewEntity}
            onSingleFilingPaid={handleSingleFilingPaid}
          />

          {chipYears.length > 0 && (
            <div className="t-sticky t-bottom-0 t-bg-surface t-pb-1 t-pt-1">
              <PastYearsCard
                years={chipYears}
                selectedYears={selectedYears}
                entityName={selectedEntity.name}
                onAddYear={openAddYear}
                onRemoveYear={openRemoveYear}
              />
            </div>
          )}
        </div>
      </div>

      <SetupModal
        key={selectedEntity.id}
        open={setupModal.isOpen}
        onClose={setupModal.close}
        entities={modalEntities}
        incompleteEntityIds={incompleteEntityIds}
        focusEntityId={setupFocusEntityId}
      />

      {addYearModal.isOpen && addSeason !== null && (
        <AddPriorYearModal
          open
          season={addSeason}
          entityId={selectedEntity.id}
          entityName={selectedEntity.name}
          onClose={addYearModal.close}
          onSuccess={handleAddYearSuccess}
        />
      )}

      {removeYearModal.isOpen && removeSeason !== null && (
        <RemoveSeasonModal
          open
          season={removeSeason}
          entityId={selectedEntity.id}
          entityName={selectedEntity.name}
          onClose={removeYearModal.close}
        />
      )}

      <CartModal
        open={paymentCartModal.isOpen}
        onClose={paymentCartModal.close}
        entityIdFromProps={currentPayEntityId ?? selectedEntity.id}
        handlePaymentSuccess={handleCartPaid}
        shouldRedirectToTask={false}
        hideEntitySelector
      />

      {currentPayEntity && (
        <MoreFilingsWaitingModal
          open={moreFilingsModal.isOpen}
          entityName={currentPayEntity.name}
          filingCount={countById[currentPayEntity.id] ?? 0}
          onLater={handlePayLater}
          onReviewPay={handleReviewNext}
        />
      )}

      <PaymentSuccessModal
        open={paymentSuccessModal.isOpen}
        entityName={paidEntity.name}
        filingCount={paidEntity.count}
        onDone={handleSuccessDone}
      />
    </>
  );
};

export const FilingsDashboard = () => {
  const groupId = useGroupId();
  const { authtoken } = useContext(authContext);

  const { data: taxYears, isLoading: isTaxYearsLoading } = useGetTaxYearsQuery(
    { groupId },
    { skip: !groupId }
  );

  const { data: cartTotal } = useGetEntitiesCartTotalQuery(
    { groupId },
    { skip: !groupId }
  );

  const isLoading = isTaxYearsLoading || !groupId;
  const hasEntities = (taxYears?.entities?.length ?? 0) > 0;

  const amountByEntityId = Object.fromEntries(
    (cartTotal?.entities ?? []).map((entity) => [entity.uuid, entity.amount])
  );

  const itemsCountByEntityId = Object.fromEntries(
    (cartTotal?.entities ?? []).map((entity) => [
      entity.uuid,
      entity.items_count,
    ])
  );

  return (
    <div className="t-mx-auto t-flex t-w-full t-max-w-[1180px] t-flex-col t-gap-7 t-bg-surface t-px-6 t-pb-5 t-pt-2 sm:t-px-4">
      {isLoading || !taxYears || !hasEntities ? (
        <Loader />
      ) : (
        <DashboardContent
          userName={authtoken?.first_name ?? ""}
          taxYears={taxYears}
          amountToPay={cartTotal?.total_amount ?? 0}
          cartItemsCount={cartTotal?.total_items_count}
          amountByEntityId={amountByEntityId}
          itemsCountByEntityId={itemsCountByEntityId}
          groupId={groupId}
        />
      )}
    </div>
  );
};
